/**
 * Rating options (0–3) and color helpers for checklist item badges and borders.
 */
import type { RatingOption } from '../types';

/** Valid rating values */
type ValidRating = 0 | 1 | 2 | 3;

/** Frozen to prevent accidental mutation */
const DEFAULT_OPTIONS: readonly RatingOption[] = Object.freeze([
  Object.freeze({ value: 0, label: 'Not in place', description: 'Not in place' }),
  Object.freeze({ value: 1, label: 'Partially implemented', description: 'Partially implemented' }),
  Object.freeze({ value: 2, label: 'Mostly complete', description: 'Mostly complete' }),
  Object.freeze({ value: 3, label: 'Fully implemented', description: 'Fully implemented' }),
]);

/** Consolidated color configuration for each rating value */
const RATING_COLORS: Readonly<Record<ValidRating, { border: string; badge: string }>> = Object.freeze({
  0: Object.freeze({ border: 'border-danger', badge: 'bg-red-100 text-danger border border-danger' }),
  1: Object.freeze({ border: 'border-warning', badge: 'bg-amber-100 text-warning border border-warning' }),
  2: Object.freeze({ border: 'border-blue-500', badge: 'bg-blue-100 text-blue-700 border border-blue-500' }),
  3: Object.freeze({ border: 'border-success', badge: 'bg-green-100 text-success border border-success' }),
});

/** Default colors for null/invalid ratings */
const DEFAULT_COLORS = Object.freeze({
  border: 'border-border',
  badge: 'bg-slate-200 text-text-secondary',
});

/**
 * Type guard to check if a rating is a valid value (0-3)
 */
function isValidRating(rating: number | null): rating is ValidRating {
  return rating !== null && Number.isInteger(rating) && rating >= 0 && rating <= 3;
}

/** Returns the four rating options (Not in place … Fully implemented) for each item. */
export function getRatingOptions(_itemNumber: number): readonly RatingOption[] {
  return DEFAULT_OPTIONS;
}

/** Returns a Tailwind border color class for the given rating (0–3 or null). */
export function getRatingColor(rating: number | null): string {
  if (!isValidRating(rating)) {
    return DEFAULT_COLORS.border;
  }
  return RATING_COLORS[rating].border;
}

/** Returns Tailwind classes for the rating badge background and text. */
export function getRatingBadgeColor(rating: number | null): string {
  if (!isValidRating(rating)) {
    return DEFAULT_COLORS.badge;
  }
  return RATING_COLORS[rating].badge;
}
